import { Link } from 'react-router-dom';
import { MapPin, Phone, Clock, Facebook, Instagram, Youtube } from 'lucide-react';

const TikTokIcon = ({ size = 20, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.16a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.59z" />
  </svg>
);

const SOCIAL_LINKS = [
  { name: 'Facebook', url: 'https://www.facebook.com/dhakademia', Icon: Facebook },
  { name: 'Instagram', url: 'https://www.instagram.com/az.akademia/', Icon: Instagram },
  { name: 'TikTok', url: 'https://www.tiktok.com/@az.akademia', Icon: TikTokIcon },
  { name: 'YouTube', url: 'https://www.youtube.com/@AzAkadémia', Icon: Youtube },
];

export const Footer = () => {
  return (
    <footer className="bg-[#07516B] border-t border-white/10" data-testid="footer">
      <div className="container-custom py-10 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12 items-start">
          {/* Brand */}
          <div className="-mt-6">
            <Link to="/" data-testid="footer-logo">
              <img loading="lazy"
                src="/az-akademia-logo.png"
                alt="AZ AKADÉMIA"
                className="h-16 md:h-24 w-auto bg-white rounded-lg px-4 py-3"
              />
            </Link>
            <p className="mt-2 text-white/80 text-sm font-medium">
              Akadémia Plusz 2.0 Kft.
            </p>
            <p className="mt-4 text-white/60 text-sm leading-relaxed">
              Gyakorlati készségfejlesztés több mint 20 év tapasztalattal. Modern módszertan, valódi eredmények.
            </p>
            <div className="mt-5 flex items-center gap-4">
              {SOCIAL_LINKS.map(({ name, url, Icon }) => (
                <a
                  key={name}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Az Akadémia ${name} oldal`}
                  className="text-white/70 hover:text-brand-light-green transition-colors"
                  data-testid={`footer-social-${name.toLowerCase()}`}
                >
                  <Icon size={22} />
                </a>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold mb-4">Kapcsolat</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-white/60 text-sm">
                <MapPin size={16} className="text-[#56999B] flex-shrink-0 mt-0.5" />
                <span>1027 Budapest, Kapás utca 6-12</span>
              </li>
              <li className="flex items-start gap-3 text-white/60 text-sm">
                <Phone size={16} className="text-[#56999B] flex-shrink-0 mt-0.5" />
                <div>
                  <a href="tel:+36705188091" className="hover:text-brand-light-green transition-colors block">
                    +36 70 518 8091
                  </a>
                  <a href="tel:+3615552222" className="hover:text-brand-light-green transition-colors block">
                    +36 (1) 555-2222
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3 text-white/60 text-sm">
                <Clock size={16} className="text-[#56999B] flex-shrink-0 mt-0.5" />
                <div>
                  <span className="block">H-CS: 09:00-17:30</span>
                  <span className="block">P: 09:00-16:00</span>
                </div>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Jogi információk</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/aszf" className="text-white/60 hover:text-brand-light-green text-sm transition-colors">
                  ÁSZF
                </Link>
              </li>
              <li>
                <Link to="/adatvedelem" className="text-white/60 hover:text-brand-light-green text-sm transition-colors">
                  Adatkezelési tájékoztató
                </Link>
              </li>
              <li>
                <Link to="/impresszum" className="text-white/60 hover:text-brand-light-green text-sm transition-colors">
                  Impresszum
                </Link>
              </li>
              <li>
                <Link to="/cookie-tajekoztato" className="text-white/60 hover:text-brand-light-green text-sm transition-colors">
                  Cookie tájékoztató
                </Link>
              </li>
              <li>
                <button
                  onClick={() => window.dispatchEvent(new Event('reopen-cookie-consent'))}
                  className="text-white/60 hover:text-brand-light-green text-sm transition-colors"
                >
                  Cookie beállítások
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-8 md:mt-12 pt-6 md:pt-8 flex flex-col items-center">
          <img loading="lazy"
            src="/akademia-duna-house-csoport-tagja.webp"
            alt="Akadémia 2.0 – A Duna House Csoport tagja"
            className="h-16 md:h-32 w-auto mb-6 bg-white/85 rounded-lg px-2 py-0.5"
          />
        </div>

        <div className="text-center space-y-2">
          <p className="text-white/40 text-sm">
            © 2026 Akadémia Plusz 2.0 Kft. Minden jog fenntartva.
          </p>
          <p className="text-white/30 text-xs">
            A weboldalt készítette:{' '}
            <a
              href="https://scrolldigital.hu"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-brand-light-green transition-colors"
            >
              Scroll Studio Kft.
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};
